import { XCircleIcon } from "@heroicons/react/24/outline";
import { clsx } from "@mantine/core";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetCancelledOffersQuery } from "graphql/queries/__generated__/publish-offers";
import OfferThreadContainer from "./OfferThreadContainer";
import CancelledDocumentViewer from "./CancelledDocumentViewer";

type CancelledOffer = NonNullable<NonNullable<GetCancelledOffersQuery['getCancelledOffers']>[number]>;

interface CancelledOfferCardProps {
    offer: CancelledOffer;
}

const CancelledOfferCard: FC<CancelledOfferCardProps> = ({ offer }) => {
    const { t } = useTranslation();
    const [threadOpen, setThreadOpen] = useState(false);
    const [docsOpen, setDocsOpen] = useState(false);

    const cardBaseClasses =
        "relative bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 ease-in-out w-full min-w-[320px] border";
    const cardHoverClasses = "hover:shadow-xl hover:border-gray-400";
    const cardCancelledClasses = "border-l-4 border-l-gray-500 border-gray-200";

    const offerId = offer.offer_id || offer.id;
    const participationId = offer.offer_published?.reinsurer_participation?.offer_published_participant_id || undefined;
    const unreadThreads = (offer as any).unread_transaction_threads ?? [];
    const hasThread = unreadThreads.length > 0;
    const unreadMessageIds = unreadThreads
        .filter((msg: any) => msg !== null)
        .map((msg: any) => msg?.id)
        .filter(Boolean) as string[];

    const businessName =
        offer.classofbusiness?.business_name ||
        offer.class_of_business?.business_name;

    const offerPublished = {
        offer_published_id: offerId,
        published_by: "PLACEIT",
        offer: {
            offer_id: offerId,
            id: offerId,
            offer_detail: offer.offer_detail,
            facultative_offer: offer.facultative_offer,
            classofbusiness: { business_name: businessName },
            class_of_business: { business_name: businessName },
        },
    } as any;

    return (
        <>
            <div
                className={clsx(
                    cardBaseClasses,
                    cardHoverClasses,
                    cardCancelledClasses,
                )}>
                <div className="absolute top-3 right-3 z-10 flex flex-col items-end space-y-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-gray-100 text-gray-700 border border-gray-300">
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        {t("pages.all_business.cancelled_card.cancelled")}
                    </span>
                </div>

                <div className="p-5">
                    <div className="mb-4 pr-16">
                        <h3
                            className="text-lg font-semibold text-gray-800 truncate"
                            title={
                                offer.offer_detail?.insured_by || t("common.na")
                            }>
                            {offer.offer_detail?.insured_by || t("common.na")}
                        </h3>
                        <p className="text-md text-gray-600 font-bold truncate">
                            {offer.insurer?.insurer_company_name || t("common.na")}
                        </p>
                        <p className="text-sm text-gray-600 truncate">
                            {t("pages.all_business.cancelled_card.policy")}{" "}
                            {offer.offer_detail?.policy_number || t("common.na")}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("pages.all_business.offer_item.policy_number")}
                            </p>
                            <p className="font-medium text-gray-700 truncate">
                                {offer.offer_detail?.policy_number || t("common.na")}
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("pages.all_business.cancelled_card.source")}
                            </p>
                            <p className="font-medium text-gray-700">
                                {offer.offer_from || t("common.na")}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("offer_details.currency")}
                            </p>
                            <p className="font-medium text-gray-700">
                                {offer.offer_detail?.currency || t("common.na")}
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("pages.all_business.offer_item.policy_number")}
                            </p>
                            <p className="font-medium text-gray-700 truncate">
                                {offer.offer_detail?.policy_number ||
                                    t("common.na")}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("pages.all_business.cancelled_card.period_from")}
                            </p>
                            <p className="font-medium text-gray-700">
                                {offer.offer_detail?.period_of_insurance_from
                                    ? new Date(
                                          offer.offer_detail
                                              .period_of_insurance_from,
                                      ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                      })
                                    : t("common.na")}
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("pages.all_business.cancelled_card.period_to")}
                            </p>
                            <p className="font-medium text-gray-700">
                                {offer.offer_detail?.period_of_insurance_to
                                    ? new Date(
                                          offer.offer_detail.period_of_insurance_to,
                                      ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                      })
                                    : t("common.na")}
                            </p>
                        </div>
                    </div>

                    {offer.offer_cancel_reason && (
                        <div className="mt-4 bg-gray-50 p-3 rounded-md border border-gray-300">
                            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-1">
                                {t(
                                    "pages.all_business.cancelled_card.cancellation_reason",
                                )}
                            </p>
                            <p className="text-sm text-gray-800">
                                {offer.offer_cancel_reason}
                            </p>
                        </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                        <button
                            onClick={() => setThreadOpen(true)}
                            className="relative inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            {t("pages.all_business.action_buttons.thread")}
                            {hasThread && (
                                <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-orange-500 rounded-full">
                                    {unreadThreads.length}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setDocsOpen(true)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {t("pages.all_business.action_buttons.docs")}
                        </button>
                    </div>
                </div>
            </div>

            <OfferThreadContainer
                open={threadOpen}
                setOpen={setThreadOpen}
                offerPublished={offerPublished}
                participationId={participationId}
                unreadMessageIds={unreadMessageIds}
            />

            <CancelledDocumentViewer
                open={docsOpen}
                setOpen={setDocsOpen}
                offer={{
                    offer_id: offer.offer_id,
                    id: offer.id,
                    offer_from: offer.offer_from,
                    published_by: offer.offer_published?.published_by,
                    offer_detail: offer.offer_detail,
                }}
            />
        </>
    );
};

export default CancelledOfferCard;
